"use client";

import { useEffect, useRef, useState } from "react";
import { Pause, Play, Rewind, FastForward } from "lucide-react";
import { cn } from "@/components/ui/cn";

/**
 * Apple-Podcasts-style audio player for the prominent single-episode slot.
 *
 * Design:
 *   - Big circular play/pause button in brand gold (Apple's primary CTA size,
 *     ~64-72px hit target).
 *   - Skip back 15s / skip forward 30s flanking it (standard podcast intervals).
 *   - Clean horizontal scrub bar with click-to-seek + a gold-accent thumb that
 *     grows on hover.
 *   - Time codes (current / remaining) under the scrub bar in tabular nums.
 *   - No native browser <audio controls> chrome — we render our own UI and use
 *     <audio> only as the source/state engine.
 */
export function PodcastPlayer({
  src,
  title,
  className,
}: {
  src: string;
  title?: string;
  className?: string;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setCurrent(el.currentTime);
    const onLoaded = () => {
      setDuration(el.duration || 0);
      setReady(true);
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
    };
  }, []);

  function toggle() {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play();
    else el.pause();
  }

  function skip(seconds: number) {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = Math.max(0, Math.min(el.duration || 0, el.currentTime + seconds));
  }

  function onSeek(e: React.ChangeEvent<HTMLInputElement>) {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = Number(e.target.value);
  }

  const progress = duration > 0 ? (current / duration) * 100 : 0;
  const remaining = Math.max(0, (duration || 0) - current);

  return (
    <div className={cn("w-full", className)}>
      <audio ref={audioRef} src={src} preload="metadata" className="hidden" />

      {/* Scrub bar */}
      <div className="relative">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={current}
          onChange={onSeek}
          aria-label={title ?? "Audio scrubber"}
          disabled={!ready}
          className="podcast-player__scrub w-full appearance-none bg-transparent cursor-pointer disabled:cursor-not-allowed"
          style={
            {
              ["--progress" as string]: `${progress}%`,
            } as React.CSSProperties
          }
        />
        {/* Visible track, drawn under the native range input via CSS in globals */}
      </div>

      {/* Time codes */}
      <div className="mt-1.5 flex items-center justify-between text-[11px] tabular-nums font-display font-bold text-foreground/55">
        <span>{fmtTime(current)}</span>
        <span>-{fmtTime(remaining)}</span>
      </div>

      {/* Transport row */}
      <div className="mt-3 flex items-center justify-center gap-4 md:gap-6">
        <button
          type="button"
          onClick={() => skip(-15)}
          aria-label="Skip back 15 seconds"
          disabled={!ready}
          className="relative size-11 grid place-items-center rounded-full text-foreground/75 hover:text-foreground hover:bg-foreground/[0.05] transition-colors disabled:opacity-50 disabled:pointer-events-none"
        >
          <Rewind className="size-5" aria-hidden />
          <span className="absolute -bottom-0.5 right-1 text-[8px] font-display font-extrabold tabular-nums text-foreground/55">
            15
          </span>
        </button>

        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? "Pause" : "Play"}
          disabled={!ready}
          className="size-16 md:size-[72px] grid place-items-center rounded-full bg-accent text-accent-foreground transition-all duration-200 ease-out hover:-translate-y-0.5 hover:brightness-105 active:translate-y-0 active:brightness-100 disabled:opacity-60 disabled:pointer-events-none"
          style={{
            boxShadow:
              "0 2px 4px rgb(0 0 0 / 0.10), 0 10px 24px -8px color-mix(in srgb, color-mix(in srgb, var(--color-accent), black 45%) 50%, transparent), inset 0 1px 0 color-mix(in srgb, color-mix(in srgb, var(--color-accent), white 80%) 70%, transparent), inset 0 -1px 0 color-mix(in srgb, color-mix(in srgb, var(--color-accent), black 40%) 20%, transparent)",
          }}
        >
          {playing ? (
            <Pause className="size-7 md:size-8 fill-current" aria-hidden />
          ) : (
            <Play className="size-7 md:size-8 ml-0.5 fill-current" aria-hidden />
          )}
        </button>

        <button
          type="button"
          onClick={() => skip(30)}
          aria-label="Skip forward 30 seconds"
          disabled={!ready}
          className="relative size-11 grid place-items-center rounded-full text-foreground/75 hover:text-foreground hover:bg-foreground/[0.05] transition-colors disabled:opacity-50 disabled:pointer-events-none"
        >
          <FastForward className="size-5" aria-hidden />
          <span className="absolute -bottom-0.5 left-1 text-[8px] font-display font-extrabold tabular-nums text-foreground/55">
            30
          </span>
        </button>
      </div>
    </div>
  );
}

function fmtTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
